<script lang="ts">
import Vue from 'vue'
import {Component, Prop, Watch} from 'vue-property-decorator'
import {groupBy, sortBy} from 'lodash'

import api from '@/api'
import {ApexOptions} from 'apexcharts'

// let chartColors = ['#42c4d7']

@Component({})
export default class SpaceChartSensoryData extends Vue {
  @Prop()
  spaceId: string

  interval: string = '30d'
  groupByTime: 'HOUR' | 'DAY' | 'MONTH' = 'DAY'

  height = 250
  chartFillOptions = {
    type: 'gradient',
    gradient: {
      shadeIntensity: 1,
      inverseColors: true,
      opacityFrom: 0.8,
      opacityTo: 0,
      stops: [0, 90, 100],
    },
  }

  promise: Promise<any> = null
  apiData: any = {}
  allEvents: any[] = []

  get temperatureChartOptions(): ApexOptions {
    return {
      chart: {
        id: 'sensory-data-temp',
        // group: 'sensory-data',
        type: 'line',
      },
      xaxis: {
        type: 'datetime',
      },
      yaxis: {
        min: Math.min(50, ...this.temperatureEvents.map((event) => event.averageValue)),
        max: Math.max(90, ...this.temperatureEvents.map((event) => event.averageValue)),
        labels: {
          formatter(val: number) {
            return val.toFixed(0) + ' °F'
          },
        },
      },
      tooltip: {
        x: {
          formatter(val) {
            return new Date(val).toLocaleString()
          },
        },
      },
      annotations: {
        yaxis: [
          {
            y: this.apiData.tempHighLimit,
            borderColor: '#fd5248',
            borderWidth: 2,
            label: {
              text: 'High Temperature',
            },
          },
          {
            y: this.apiData.tempLowLimit,
            borderColor: '#3d9ae8',
            borderWidth: 2,
            label: {
              text: 'Low Temperature',
            },
          },
        ],
      },
    }
  }

  get lightChartOptions(): ApexOptions {
    return {
      chart: {
        id: 'sensory-data-light',
        // group: 'sensory-data',
        type: 'line',
      },
      xaxis: {
        type: 'datetime',
      },
      tooltip: {
        x: {
          formatter(val) {
            return new Date(val).toLocaleString()
          },
        },
        y: {
          formatter(val) {
            return val + ' lm'
          },
        },
      },
    }
  }

  get vocChartOptions(): ApexOptions {
    return {
      chart: {
        id: 'sensory-data-voc',
        // group: 'sensory-data',
        type: 'area',
      },
      fill: this.chartFillOptions,
      dataLabels: {
        enabled: false,
      },
      xaxis: {
        type: 'datetime',
      },
      yaxis: {
        min: 0,
        max: Math.max(
          this.apiData.vocHighLimit,
          ...this.vocEvents.map((event) => event.averageValue)
        ),
      },
      tooltip: {
        x: {
          formatter(val) {
            return new Date(val).toLocaleString()
          },
        },
        y: {
          formatter(val) {
            return val + ' ppb'
          },
        },
      },
      annotations: {
        yaxis: [
          {
            y: this.apiData.vocHighLimit,
            borderColor: '#FF4560',
            borderWidth: 2,
            label: {
              text: 'High VOC',
            },
          },
          {
            y: this.apiData.vocWarningLimit,
            borderColor: '#ffc107',
            borderWidth: 2,
            label: {
              text: 'Warning VOC',
            },
          },
        ],
      },
    }
  }

  get co2ChartOptions(): ApexOptions {
    return {
      chart: {
        id: 'sensory-data-co2',
        // group: 'sensory-data',
        type: 'area',
      },
      fill: this.chartFillOptions,
      dataLabels: {
        enabled: false,
      },
      xaxis: {
        type: 'datetime',
      },
      yaxis: {
        min: 0,
        max: Math.max(
          this.apiData.co2HighLimit,
          ...this.co2Events.map((event) => event.averageValue)
        ),
      },
      tooltip: {
        x: {
          formatter(val) {
            return new Date(val).toLocaleString()
          },
        },
        y: {
          formatter(val) {
            return val + ' ppm'
          },
        },
      },
      annotations: {
        yaxis: [
          {
            y: this.apiData.co2HighLimit,
            borderColor: '#FF4560',
            borderWidth: 2,
            label: {
              text: 'High CO2',
            },
          },
          {
            y: this.apiData.co2WarningLimit,
            borderColor: '#ffc107',
            borderWidth: 2,
            label: {
              text: 'Warning CO2',
            },
          },
        ],
      },
    }
  }

  get temperatureEvents() {
    return this.allEvents.filter((event) => event.sensorType == 'TEMPERATURE')
  }

  get lightEvents() {
    return this.allEvents.filter((event) => event.sensorType == 'LIGHT')
  }

  get vocEvents() {
    return this.allEvents.filter((event) => event.sensorType == 'VOC')
  }

  get co2Events() {
    return this.allEvents.filter((event) => event.sensorType == 'CO2')
  }

  get temperatureSeries(): any[] {
    return this.groupEventsIntoSeries(this.temperatureEvents, 'locationName')
  }

  get lightSeries(): any[] {
    return this.groupEventsIntoSeries(this.lightEvents, 'locationName')
  }

  get vocSeries(): any[] {
    return this.groupEventsIntoSeries(this.vocEvents, 'locationName')
  }

  get co2Series(): any[] {
    return this.groupEventsIntoSeries(this.co2Events, 'locationName')
  }

  groupEventsIntoSeries(events, byKey: string) {
    let grouped = groupBy(events, byKey)
    let ret = []

    for (let k in grouped) {
      ret.push({
        name: k,
        data: grouped[k].map((group) => {
          return [new Date(group.timestamp * 1000), group.averageValue.toFixed(0)]
        }),
      })
    }

    ret = sortBy(ret, (series) => series.name)
    return ret
  }

  setTimeframe(interval: string, groupByTime: any) {
    this.apiData = {}
    this.allEvents = []
    this.interval = interval
    this.groupByTime = groupByTime

    this.promise = api.spaces
      .chartSensoryData(this.spaceId, {
        interval: interval,
        group_by_time: groupByTime,
      })
      .then((resp) => {
        this.apiData = resp.data
        resp.data.deviceSensoryDetailsDtoList.reverse()
        this.allEvents = resp.data.deviceSensoryDetailsDtoList
      })
  }

  async mounted() {
    this.setTimeframe(this.interval, this.groupByTime)
  }
}
</script>
|
<template>
  <div>
    <h4 class="mt-5 mb-3">
      Sensors ({{ interval }})

      <span class="small">
        <b-link @click="setTimeframe('24h', 'HOUR')" class="ml-1 small">Day</b-link> &centerdot;
        <b-link @click="setTimeframe('7d', 'HOUR')" class="ml-1 small">Week</b-link> &centerdot;
        <b-link @click="setTimeframe('30d', 'DAY')" class="ml-1 small">Month</b-link> &centerdot;
        <b-link @click="setTimeframe('90d', 'DAY')" class="ml-1 small">3 Months</b-link>
      </span>
    </h4>

    <div class="row mt-4">
      <div class="col">
        <div class="card card-body">
          <promised :promise="promise" :pending-delay="0">
            <template #pending>
              <div class="spinner-border mx-auto" role="status"></div>
            </template>
            <template #rejected>
              <div class="card-subtitle text-danger mt-2">Error loading data</div>
            </template>
            <template>
              <div class="row" v-if="allEvents.length">
                <div class="col-md-6">
                  <div class="p-3">
                    <h5>
                      <font-awesome-icon icon="thermometer-half" class="fa-fw"></font-awesome-icon>
                      Temperature<small class="ml-1 text-muted">(°F)</small>
                    </h5>
                  </div>
                  <div v-if="apiData">
                    <apexchart
                      :height="height"
                      :options="temperatureChartOptions"
                      :series="temperatureSeries"></apexchart>
                  </div>
                </div>
                <div class="col-md-6">
                  <div class="p-3">
                    <h5>
                      <font-awesome-icon icon="lightbulb" class="fa-fw"></font-awesome-icon>
                      Light<small class="ml-1 text-muted">(Lumens)</small>
                    </h5>
                  </div>
                  <div v-if="apiData">
                    <apexchart
                      :height="height"
                      :options="lightChartOptions"
                      :series="lightSeries"></apexchart>
                  </div>
                </div>
                <div class="col-md-6">
                  <div class="p-3">
                    <h5>
                      VOC <small class="ml-1 text-muted">Volatile Organic Compounds (ppb)</small>
                    </h5>
                  </div>
                  <div v-if="apiData">
                    <apexchart
                      :height="height"
                      :options="vocChartOptions"
                      :series="vocSeries"></apexchart>
                  </div>
                </div>
                <div class="col-md-6">
                  <div class="p-3">
                    <h5>
                      CO<sub>2</sub>
                      <small class="ml-1 text-muted">Carbon Dioxide (ppm)</small>
                    </h5>
                  </div>
                  <div v-if="apiData">
                    <apexchart
                      :height="height"
                      :options="co2ChartOptions"
                      :series="co2Series"></apexchart>
                  </div>
                </div>
              </div>
              <div v-else>
                <div class="card-subtitle text-muted mt-2">No data</div>
              </div>
            </template>
          </promised>
        </div>
      </div>
    </div>
  </div>
</template>
